import {FormControlLabel, Switch} from '@mui/material'
import {Box, Column, Modal, Table, TableData} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useState} from 'react'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import AppAddOrOptionsButton from 'src/components/AppAddOrOptionsButton'
import LinkWithAction from 'src/components/LinkWithAction'
import RowHover from 'src/components/RowHover/RowHover'
import useObjects from 'src/hooks/api/objects/useObjects'
import useOutputServers from 'src/hooks/api/server/useOutputServers'
import StepperModal from 'src/pages/camera-config/components/object-detection/components/stepperModal/StepperModal'
import {CameraHasObjects} from 'src/services/api/endpoints/ObjectEndpoint'
import {useWindowSize} from 'usehooks-ts'
import {useCameraConfigContext} from '../../context/CameraConfigContext'
import MenuMoreInformationsObject from './MoreInformationsObject'
import {RowType, deleteObject, objectDetectionRow, updateIsActive} from './ObjectDetectionFunctions'

const columns: Column[] = [
  {
    id: 'typeObject',
    name: 'Tipo do Objeto',
  },
  {
    id: 'barrierType',
    name: 'Tipo da Barreira',
  },
  {
    id: 'barrier',
    name: 'Barreira',
  },
  {
    id: 'confidence',
    name: 'Nível de Confiança',
  },
  {
    id: 'drive',
    name: 'Acionamento',
  },
  {
    id: 'active',
    name: 'Ativo',
  },
  {
    id: 'actions',
    name: 'Ações',
  },
]

export type ObjectActionType = {
  outputIntegrationId?: number | string
  boxOutputDeviceId?: number
  telegramChatId?: number
  listId?: number
  idToDelete: number
  fetchVehicleInfoFromSinesp?: boolean
  fetchVehicleInfoFromFipe?: boolean
  checkPlateIsRobbed?: boolean
  dispatchAllPlates?: boolean
  dispatchToEmail?: string
  dispatchPlateToHarpia?: boolean
  dispatchPlateToSpia?: boolean
  dispatchPlateToSivana?: boolean
  integrationCode?: string
  actionColor?: string
  whatsappLink?: string
  dispatchPlateToAspide?: boolean
  sigmaValidationId?: number
  validationChatId?: number
  userIdHasNotification?: number
  dispatchPlateToMuralhaPaulista?: boolean
}

export type MappedActions = {
  id: number
  objectAction: ObjectActionType[]
}

export function ObjectDetection() {
  const {camera} = useCameraConfigContext()
  const {enqueueSnackbar} = useSnackbar()

  const [isActive, setIsActive] = useState(false)
  const [isCreate, setIsCreate] = useState(false)
  const [openObject, setOpenObject] = useState(false)
  const [selfObject, setSelfObject] = useState<CameraHasObjects>()

  const windowSize = useWindowSize()
  const userHasUpdateCameraPermission = ValidateUserPermissions('cameras:update')

  const {
    data: allObjectsOnCamera,
    refetch,
    isLoading,
  } = useObjects({
    cameraId: camera?.id ?? -1,
    includes: [
      'interestArea',
      'objectType',
      'actions',
      'speedObject',
      'epiDetectionObject',
      'vehicleDetectionObject',
      'animalDetectionObject',
      'objectsDetectionObject',
    ],
    enabled: !!camera?.id,
  })

  const mappedActions: MappedActions[] =
    allObjectsOnCamera?.map((it) => {
      const objectAction: ObjectActionType[] = []
      it.actions.forEach((action) => {
        objectAction.push({
          ...action,
          outputIntegrationId: action.outputIntegrationId || undefined,
          listId: action.faceListId || action.licensePlateListId || (action.blockUnknown ? -1 : undefined),
          telegramChatId: action.telegramChatId || undefined,
          idToDelete: action.id,
          validationChatId: action.validationChatId || undefined,
          sigmaValidationId: action.sigmaValidationId || undefined,
        })
      })

      const action = {
        id: it.id,
        objectAction,
      }
      return action
    }) ?? []

  function handleRowClick(row: RowType) {
    setIsCreate(false)
    setSelfObject(row.self)
    setOpenObject(true)
  }

  const {data: servers} = useOutputServers({
    params: {
      page: 1,
      pageSize: 20,
      filter: {integrationType: 'SAÍDA', companyIds: [camera?.companyId ?? -1]},
      includes: ['serverType'],
    },
    enabled: !!camera,
  })

  return (
    <Box>
      <Box display='flex' justify='flex-end'>
        <Modal
          isOpen={openObject}
          paddingBottom='16px'
          paddingLeft='16px'
          paddingRight='16px'
          paddingTop='16px'
          children={
            <StepperModal
              closeModal={setOpenObject}
              registeredObjects={allObjectsOnCamera}
              isEdit={!isCreate}
              editingObject={selfObject}
              mappedActions={mappedActions}
              servers={servers?.entities ?? []}
              updateTable={refetch}
            />
          }
          closeModal={() => setOpenObject(false)}
          backgroundColor='white'
          width={windowSize.width <= 1200 ? '80%' : '65%'}
        />
        <AppAddOrOptionsButton
          text='Adicionar'
          onClickAdd={() => {
            setOpenObject(true)
            setIsCreate(true)
          }}
          showOptions={false}
        />
      </Box>

      <Box overflow='auto' height='500px' marginTop='20px' paddingBottom='60px'>
        <Table isLoading={isLoading} columns={columns} headerFontSize='14px' skeletonRowsAmount={3}>
          {objectDetectionRow(allObjectsOnCamera ?? []).map((row) => (
            <RowHover id={`${row.id}-${row.barrierType}`} key={row.id}>
              <TableData>
                <LinkWithAction onClick={() => handleRowClick(row)}>{row.typeObject}</LinkWithAction>
              </TableData>
              <TableData>{row.barrierType}</TableData>
              <TableData>{row.barrier}</TableData>
              <TableData>{row.confidence}</TableData>
              <TableData>{row.drive}</TableData>
              <TableData>
                <Box width='100%' display='flex' justify='flex-start' align='center'>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={row.active}
                        onChange={async (e) => {
                          const checked = e.target.checked
                          await updateIsActive(checked, row, enqueueSnackbar, refetch)
                          setIsActive(!isActive)
                        }}
                        inputProps={{'aria-label': 'controlled'}}
                      />
                    }
                    label={undefined}
                  />
                </Box>
              </TableData>
              <TableData>
                <MenuMoreInformationsObject
                  oneObject={row.id}
                  onClick={(event) => {
                    if (event === 'edit') {
                      setIsCreate(false)
                      setSelfObject(row.self)
                      setOpenObject(true)
                    }
                    if (event === 'delete') {
                      if (userHasUpdateCameraPermission) deleteObject(row.self, refetch, enqueueSnackbar)
                      else enqueueSnackbar('Você não tem permissão para editar configuração de câmeras', {variant: 'warning'})
                    }
                  }}
                />
              </TableData>
            </RowHover>
          ))}
        </Table>
      </Box>
    </Box>
  )
}
